import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DonateSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const pollStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus("timeout");
        return;
      }

      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/donate/status/${sessionId}`
        );
        const data = response.data;

        if (data.payment_status === "paid") {
          setStatus("success");
          setPaymentInfo(data);
          return;
        } else if (data.status === "expired") {
          setStatus("expired");
          return;
        }

        // Continue polling
        setTimeout(() => pollStatus(attempts + 1), pollInterval);
      } catch (error) {
        // Error handled via UI state
        if (attempts >= maxAttempts - 1) {
          setStatus("error");
        } else {
          setTimeout(() => pollStatus(attempts + 1), pollInterval);
        }
      }
    };

    pollStatus();
  }, [sessionId]);

  return (
    <div data-testid="donate-success-page" className="pt-16 md:pt-20 min-h-screen bg-[#F8F9FA]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="bg-white border border-slate-200 p-8 md:p-12 text-center">
          {status === "loading" && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-[#002B7F] animate-spin mb-6" />
              <h1 className="text-2xl font-bold text-slate-900 mb-4">
                Processing Your Donation
              </h1>
              <p className="text-slate-600">
                Please wait while we confirm your contribution...
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-green-100 rounded-full">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-4">
                Thank You!
              </h1>
              <p className="text-lg text-slate-600 mb-2">
                Your donation of{" "}
                <span className="font-bold text-[#002B7F]">
                  ${paymentInfo?.amount_total ? (paymentInfo.amount_total / 100).toFixed(2) : ""}
                </span>{" "}
                has been received.
              </p>
              <p className="text-slate-500 mb-8">
                Together, we're building a Nevada where every family can thrive.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/"
                  data-testid="success-home-btn"
                  className="px-8 py-4 bg-[#002B7F] hover:bg-[#001F5C] text-white font-bold uppercase tracking-widest text-sm transition-all"
                >
                  Return Home
                </Link>
                <Link
                  to="/volunteer"
                  data-testid="success-volunteer-btn"
                  className="px-8 py-4 border-2 border-[#002B7F] text-[#002B7F] hover:bg-[#002B7F] hover:text-white font-bold uppercase tracking-widest text-sm transition-all"
                >
                  Volunteer
                </Link>
              </div>
            </>
          )}

          {(status === "error" || status === "timeout" || status === "expired") && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-red-100 rounded-full">
                <XCircle className="w-12 h-12 text-red-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-4">
                {status === "expired" ? "Session Expired" : "Something Went Wrong"}
              </h1>
              <p className="text-slate-600 mb-8">
                {status === "expired"
                  ? "Your payment session has expired. Please try again."
                  : "We couldn't confirm your donation. Please check your email for confirmation or try again."}
              </p>
              <Link
                to="/donate"
                data-testid="error-try-again-btn"
                className="inline-block px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all"
              >
                Try Again
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DonateSuccessPage;
